import { getSupabaseClient } from "@veorest/api-client";
import type { TranslationKey } from "../i18n";

export type PartnerDriverPresence = "online" | "busy" | "offline";

/** OWN = partner keeps the full delivery fee; PAID = tracked via driver wallet. */
export type PartnerDriverMode = "own" | "paid";

export interface PartnerDriverRow {
  id: string;
  name: string;
  phone: string | null;
  fleet_type: string;
  partner_id: string | null;
  is_online: boolean;
  driver_status: string | null;
  busy_remaining_seconds: number;
  is_active: boolean;
  vehicle_type: string | null;
  vehicle_number: string | null;
  active_orders: number;
  driver_mode: PartnerDriverMode | null;
  /** 170: per-trip pay override (IQD). Null = partner default applies. */
  trip_fee_amount: number | null;
  /** 240: exposed profile fields (existing columns). */
  username: string | null;
  vehicle_model: string | null;
  vehicle_year: number | null;
  vehicle_color: string | null;
  license_image_url: string | null;
  photo_url: string | null;
}

export interface CreatePartnerDriverInput {
  name: string;
  phone: string;
  vehicleType?: "bicycle" | "motorcycle" | "car";
  vehicleNumber?: string;
  collectsCod?: boolean;
  driverMode?: PartnerDriverMode;
}

export interface CreatePartnerDriverResult {
  driver: PartnerDriverRow;
  passcode: string | null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function str(v: unknown, fallback = ""): string {
  if (v == null) return fallback;
  return String(v);
}

function num(v: unknown, fallback = 0): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function partnerDriverPresence(
  driver: Pick<PartnerDriverRow, "is_online" | "driver_status">
): PartnerDriverPresence {
  if (!driver.is_online) return "offline";
  if (driver.driver_status === "busy") return "busy";
  return "online";
}

export function partnerDriverPresenceLabel(presence: PartnerDriverPresence): string {
  switch (presence) {
    case "online":
      return "Online";
    case "busy":
      return "Busy";
    default:
      return "Offline";
  }
}

/** Online + busy can receive blasts/assignments (matches Flutter Partner). */
export function partnerDriverIsAssignable(
  driver: Pick<PartnerDriverRow, "is_online" | "driver_status">
): boolean {
  return partnerDriverPresence(driver) !== "offline";
}

export function partnerSortDrivers(drivers: PartnerDriverRow[]): PartnerDriverRow[] {
  const rank = (d: PartnerDriverRow) => {
    const p = partnerDriverPresence(d);
    if (p === "online") return 0;
    if (p === "busy") return 1;
    return 2;
  };
  return [...drivers].sort((a, b) => {
    const byPresence = rank(a) - rank(b);
    if (byPresence !== 0) return byPresence;
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  });
}

export function partnerAssignableDrivers(
  drivers: PartnerDriverRow[]
): PartnerDriverRow[] {
  return partnerSortDrivers(drivers).filter(partnerDriverIsAssignable);
}

export function mapPartnerDriver(
  raw: Record<string, unknown>,
  activeOrders = 0
): PartnerDriverRow {
  return {
    id: str(raw.id),
    name: str(raw.name, "Driver"),
    phone: raw.phone != null ? str(raw.phone) : null,
    fleet_type: str(raw.fleet_type, "partner"),
    partner_id: raw.partner_id != null ? str(raw.partner_id) : null,
    is_online: Boolean(raw.is_online),
    driver_status: raw.driver_status != null ? str(raw.driver_status) : null,
    busy_remaining_seconds: num(raw.busy_remaining_seconds),
    is_active: raw.is_active === false ? false : true,
    vehicle_type: raw.vehicle_type != null ? str(raw.vehicle_type) : null,
    vehicle_number:
      raw.vehicle_number != null
        ? str(raw.vehicle_number)
        : raw.vehicle_plate != null
          ? str(raw.vehicle_plate)
          : null,
    active_orders: activeOrders,
    driver_mode:
      raw.partner_driver_mode === "paid" || raw.partner_driver_mode === "own"
        ? raw.partner_driver_mode
        : raw.driver_relationship_type === "EMPLOYED"
          ? "own"
          : raw.driver_relationship_type === "HIRED_CONTRACTOR"
            ? "paid"
            : null,
    trip_fee_amount:
      raw.trip_fee_amount == null ? null : num(raw.trip_fee_amount),
    username: raw.username != null ? str(raw.username) : null,
    vehicle_model: raw.vehicle_model != null ? str(raw.vehicle_model) : null,
    vehicle_year: raw.vehicle_year == null ? null : num(raw.vehicle_year),
    vehicle_color: raw.vehicle_color != null ? str(raw.vehicle_color) : null,
    license_image_url:
      raw.license_image_url != null ? str(raw.license_image_url) : null,
    photo_url: raw.photo_url != null ? str(raw.photo_url) : null,
  };
}

export function vehicleLabel(driver: PartnerDriverRow): string {
  const type = driver.vehicle_type
    ? driver.vehicle_type.charAt(0).toUpperCase() + driver.vehicle_type.slice(1)
    : null;
  if (type && driver.vehicle_number) return `${type} · ${driver.vehicle_number}`;
  if (type) return type;
  if (driver.vehicle_number) return driver.vehicle_number;
  return "—";
}

/** Partner fleet only — existing RPC already scopes by partner_id + fleet_type. */
export async function listPartnerDrivers(
  partnerId: string
): Promise<PartnerDriverRow[]> {
  const supabase = getSupabaseClient();
  // 239: directory listing — includes inactive drivers (dispatch RPC stays active-only).
  const { data, error } = await supabase.rpc("partner_list_fleet_drivers", {
    p_partner_id: partnerId,
  });
  if (error) throw error;
  const row = asRecord(data) ?? {};
  const list = Array.isArray(row.drivers) ? row.drivers : [];

  // Active order counts from partner-scoped orders (execution filter RPC).
  const activeByDriver = new Map<string, number>();
  try {
    const { data: ordersData } = await supabase.rpc("partner_list_orders", {
      p_partner_id: partnerId,
      p_tenant_id: null,
      p_status: null,
      p_limit: 100,
      p_offset: 0,
    });
    const ordersRow = asRecord(ordersData) ?? {};
    const orders = Array.isArray(ordersRow.orders) ? ordersRow.orders : [];
    for (const item of orders) {
      const o = asRecord(item);
      if (!o) continue;
      const driverId = o.assigned_driver_id != null ? str(o.assigned_driver_id) : "";
      if (!driverId) continue;
      const status = str(o.status).toLowerCase();
      if (
        ["delivered", "completed", "cancelled", "rejected", "canceled"].includes(
          status
        )
      ) {
        continue;
      }
      activeByDriver.set(driverId, (activeByDriver.get(driverId) ?? 0) + 1);
    }
  } catch {
    // Counts are best-effort; list still works.
  }

  return list
    .map((item) => asRecord(item))
    .filter((r): r is Record<string, unknown> => !!r)
    .map((r) => mapPartnerDriver(r, activeByDriver.get(str(r.id)) ?? 0))
    .filter((d) => d.fleet_type === "partner");
}

export async function createPartnerDriver(
  partnerId: string,
  input: CreatePartnerDriverInput
): Promise<CreatePartnerDriverResult> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_create_driver", {
    p_partner_id: partnerId,
    p_name: input.name.trim(),
    p_phone: input.phone.trim(),
    p_vehicle_type: input.vehicleType ?? "motorcycle",
    p_vehicle_number: input.vehicleNumber?.trim() || null,
    p_collects_cod: input.collectsCod ?? true,
    p_driver_mode: input.driverMode ?? null,
  });
  if (error) throw error;
  const row = asRecord(data) ?? {};
  const driverRaw = asRecord(row.driver) ?? row;
  return {
    driver: mapPartnerDriver(driverRaw, 0),
    passcode: row.passcode != null ? str(row.passcode) : null,
  };
}

export async function deactivatePartnerDriver(
  partnerId: string,
  driverId: string
): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc("partner_deactivate_driver", {
    p_partner_id: partnerId,
    p_driver_id: driverId,
  });
  if (error) throw error;
}

/** 239: reactivate a deactivated partner-fleet driver. */
export async function activatePartnerDriver(
  partnerId: string,
  driverId: string
): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc("partner_activate_driver", {
    p_partner_id: partnerId,
    p_driver_id: driverId,
  });
  if (error) throw error;
}

export interface DriverUpdateInput {
  name: string;
  phone: string;
  vehicleType: "bicycle" | "motorcycle" | "car";
  vehicleNumber: string | null;
  vehicleModel: string | null;
  vehicleColor: string | null;
  vehicleYear: number | null;
}

/** 241: edit name/phone/vehicle fields (partner fleet, ops+). */
export async function updatePartnerDriver(
  partnerId: string,
  driverId: string,
  input: DriverUpdateInput
): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc("partner_update_driver", {
    p_partner_id: partnerId,
    p_driver_id: driverId,
    p_name: input.name.trim(),
    p_phone: input.phone.trim(),
    p_vehicle_type: input.vehicleType,
    p_vehicle_number: input.vehicleNumber,
    p_vehicle_model: input.vehicleModel,
    p_vehicle_color: input.vehicleColor,
    p_vehicle_year: input.vehicleYear,
  });
  if (error) throw error;
}

/** wa.me link for a driver phone (Iraq normalization: 07xx → 9647xx). */
export function driverWhatsAppLink(
  phone: string | null | undefined
): string | null {
  if (!phone) return null;
  let digits = phone.replace(/\D/g, "");
  if (!digits) return null;
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.startsWith("0")) digits = `964${digits.slice(1)}`;
  else if (/^7\d{9}$/.test(digits)) digits = `964${digits}`;
  return `https://wa.me/${digits}`;
}

export async function setPartnerDriverMode(
  partnerId: string,
  driverId: string,
  mode: PartnerDriverMode
): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc("partner_set_driver_mode", {
    p_partner_id: partnerId,
    p_driver_id: driverId,
    p_driver_mode: mode,
  });
  if (error) throw error;
}

/** 170: per-trip pay override for one fleet driver (ops+). Null clears it. */
export async function setPartnerDriverTripFee(
  partnerId: string,
  driverId: string,
  amount: number | null
): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc("partner_set_driver_trip_fee", {
    p_partner_id: partnerId,
    p_driver_id: driverId,
    p_amount: amount,
  });
  if (error) throw error;
}

export interface PartnerDriverWalletEntry {
  id: string;
  type: string;
  amount: number;
  order_id: string | null;
  description: string | null;
  created_at: string;
}

export interface PartnerDriverWallet {
  balance: number;
  currency: string;
  mode: PartnerDriverMode;
  transactions: PartnerDriverWalletEntry[];
}

function mapWalletEntry(raw: Record<string, unknown>): PartnerDriverWalletEntry {
  return {
    id: str(raw.id),
    type: str(raw.type),
    amount: num(raw.amount),
    order_id: raw.order_id != null ? str(raw.order_id) : null,
    description: raw.description != null ? str(raw.description) : null,
    created_at: str(raw.created_at),
  };
}

function mapDriverWallet(data: unknown): PartnerDriverWallet {
  const row = asRecord(data) ?? {};
  const list = Array.isArray(row.transactions) ? row.transactions : [];
  return {
    balance: num(row.balance),
    currency: str(row.currency, "IQD"),
    mode: row.mode === "paid" ? "paid" : "own",
    transactions: list
      .map((item) => asRecord(item))
      .filter((r): r is Record<string, unknown> => !!r)
      .map(mapWalletEntry),
  };
}

/** + balance = partner owes driver. */
export async function fetchPartnerDriverWallet(
  partnerId: string,
  driverId: string
): Promise<PartnerDriverWallet> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_driver_wallet_get", {
    p_partner_id: partnerId,
    p_driver_id: driverId,
  });
  if (error) throw error;
  return mapDriverWallet(data);
}

/** Signed manual entry — partner's own policy (payouts, cuts). */
export async function createPartnerDriverWalletEntry(
  partnerId: string,
  driverId: string,
  amount: number,
  note?: string
): Promise<PartnerDriverWallet> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_driver_wallet_entry", {
    p_partner_id: partnerId,
    p_driver_id: driverId,
    p_amount: amount,
    p_note: note?.trim() || null,
  });
  if (error) throw error;
  return mapDriverWallet(data);
}

export function driverModeLabelKey(mode: PartnerDriverMode | null): TranslationKey {
  return mode === "paid" ? "drivers.mode.paid" : "drivers.mode.own";
}

// ── 244: move / release lifecycle ───────────────────────────────────────────

export interface PartnerDriverLookup {
  found: boolean;
  code: "found" | "not_found" | "owned_by_you" | "owned_other";
  driver?: {
    id: string;
    name: string;
    fleet_type?: string;
    is_active?: boolean;
    vehicle_type?: string | null;
    vehicle_number?: string | null;
  };
  clients?: Array<{ tenant_id: string; name: string }>;
}

/** 244: find an existing driver by phone before moving them into the fleet. */
export async function lookupPartnerDriverByPhone(
  partnerId: string,
  phone: string
): Promise<PartnerDriverLookup> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_lookup_driver_by_phone", {
    p_partner_id: partnerId,
    p_phone: phone.trim(),
  });
  if (error) throw error;
  const row = (data ?? {}) as Record<string, unknown>;
  const driverRaw =
    row.driver && typeof row.driver === "object" && !Array.isArray(row.driver)
      ? (row.driver as Record<string, unknown>)
      : null;
  const clients = Array.isArray(row.clients)
    ? (row.clients as Array<Record<string, unknown>>).map((c) => ({
        tenant_id: String(c.tenant_id ?? ""),
        name: String(c.name ?? ""),
      }))
    : [];
  return {
    found: row.found === true,
    code: (row.code as PartnerDriverLookup["code"]) ?? "not_found",
    driver: driverRaw
      ? {
          id: String(driverRaw.id ?? ""),
          name: String(driverRaw.name ?? ""),
          fleet_type:
            driverRaw.fleet_type == null ? undefined : String(driverRaw.fleet_type),
          is_active: driverRaw.is_active === true,
          vehicle_type:
            driverRaw.vehicle_type == null ? null : String(driverRaw.vehicle_type),
          vehicle_number:
            driverRaw.vehicle_number == null ? null : String(driverRaw.vehicle_number),
        }
      : undefined,
    clients,
  };
}

export interface AssignExistingResult {
  ok: boolean;
  assigned?: boolean;
  already?: boolean;
  code?: string;
  clients?: Array<{ tenant_id: string; name: string }>;
  unlinked_clients?: Array<{ tenant_id: string; name: string }>;
}

/** 244: explicit two-step move — first call returns the client list (ok:false). */
export async function assignExistingPartnerDriver(
  partnerId: string,
  driverId: string,
  unlinkClients: boolean
): Promise<AssignExistingResult> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_assign_existing_driver", {
    p_partner_id: partnerId,
    p_driver_id: driverId,
    p_unlink_clients: unlinkClients,
  });
  if (error) throw error;
  return (data ?? {}) as AssignExistingResult;
}

/** 244: release a driver out of the fleet (parked, rejoinable elsewhere). */
export async function releasePartnerDriver(
  partnerId: string,
  driverId: string
): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc("partner_release_driver", {
    p_partner_id: partnerId,
    p_driver_id: driverId,
  });
  if (error) throw error;
}

/** Map driver RPC failures to i18n keys — call `t(mapDriversRpcError(err))` at the UI. */
export function mapDriversRpcError(err: unknown): TranslationKey {
  if (!err || typeof err !== "object") return "errors.requestFailed";
  const e = err as { message?: string };
  const msg = (e.message || "").toLowerCase();
  if (msg.includes("name_required")) return "errors.nameRequired";
  if (msg.includes("phone_invalid")) return "errors.phoneInvalid";
  if (msg.includes("phone_taken")) return "errors.phoneTaken";
  if (msg.includes("vehicle_type_invalid")) return "errors.vehicleInvalid";
  if (msg.includes("driver_not_found")) return "errors.driverNotFound";
  if (msg.includes("invalid_driver_mode")) return "errors.driverModeInvalid";
  if (msg.includes("invalid_trip_fee")) return "errors.invalidTripFee";
  if (msg.includes("partner_driver_not_in_fleet")) return "errors.driverNotFound";
  if (msg.includes("invalid_amount")) return "errors.amountInvalid";
  if (msg.includes("driver_belongs_to_other_partner")) return "errors.driverOtherPartner";
  if (msg.includes("driver_inactive")) return "errors.driverInactive";
  if (msg.includes("driver_has_active_orders")) return "errors.driverActiveOrders";
  if (msg.includes("not_partner_driver")) return "errors.driverNotFound";
  return "errors.requestFailed";
}

/** @deprecated Prefer `mapDriversRpcError` + `t(...)`. */
export function driverRpcErrorMessage(err: unknown): string {
  const key = mapDriversRpcError(err);
  const fallbacks: Record<string, string> = {
    "errors.requestFailed": "Request failed.",
    "errors.nameRequired": "Driver name is required.",
    "errors.phoneInvalid": "Enter a valid phone number.",
    "errors.phoneTaken": "That phone number is already in use.",
    "errors.vehicleInvalid": "Invalid vehicle type.",
    "errors.driverNotFound": "Driver not found in your fleet.",
  };
  return fallbacks[key] ?? "Request failed.";
}

export function driverPresenceTranslationKey(
  presence: PartnerDriverPresence
): TranslationKey {
  return `drivers.presence.${presence}` as TranslationKey;
}

export function driverVehicleTypeTranslationKey(
  vehicleType: string | null
): TranslationKey | null {
  if (vehicleType === "bicycle" || vehicleType === "motorcycle" || vehicleType === "car") {
    return `drivers.${vehicleType}` as TranslationKey;
  }
  return null;
}
